/* eslint global-require: off */
/* eslint @typescript-eslint/no-var-requires: off */

// disabled in nodejs threads
const hasDOM = typeof window !== 'undefined';
const debug = process.env.REACT_APP_MODE === 'dev';

type GlobalProperties = string | number | null;

type MixpanelInstance = {
  init(
    token: string,
    config?: Partial<{
      api_host: string;
      autotrack: boolean;
      track_pageview: true | 'url-with-path' | 'url-with-path-and-query-string' | 'full-url';
      autocapture: {
        pageview: true | 'url-with-path' | 'url-with-path-and-query-string' | 'full-url';
        click: boolean;
        input: boolean;
        scroll: boolean;
        submit: boolean;
        capture_text_content: boolean;
      };
      opt_out_tracking_by_default: boolean;
      disable_persistence: boolean;
      debug: boolean;
      ignore_dnt: boolean;
    }>
  ): void;
  opt_in_tracking(): void;
  opt_out_tracking(): void;
  reset(): void;
  identify(externalId: string): void;
  register(properties: { [index: string]: GlobalProperties }): void;
  track(event_name: string, properties?: Record<string, unknown>): void;
  people: {
    set(properties: { [index: string]: GlobalProperties }): void;
  };
};

let mixpanel: MixpanelInstance | null = null;
let mixpanelLoading: Promise<void> | null = null;

const loadMixpanel = async () => {
  if (!hasDOM || mixpanel) {
    return;
  }

  if (!mixpanelLoading) {
    mixpanelLoading = import('mixpanel-browser')
      .then((mod) => {
        mixpanel = mod.default as MixpanelInstance;
      })
      .catch((e) => {
        console.log('Mixpanel loading failed', e);
        mixpanel = null;
      });
  }

  await mixpanelLoading;
};

const init = async (apiKey: string | undefined | null) => {
  if (debug) {
    console.log('mixpanelApi.init()');
  }

  if (!hasDOM) {
    console.log('MIXPANEL disabled in context');
    return;
  }

  if (!apiKey) {
    return;
  }

  await loadMixpanel();

  if (!mixpanel) {
    console.log('MIXPANEL disabled in context');
    return;
  }

  try {
    mixpanel.init(apiKey, {
      api_host: 'https://api-eu.mixpanel.com',
      autotrack: true,
      track_pageview: 'full-url',
      autocapture: {
        pageview: 'full-url',
        click: false,
        input: false,
        scroll: false,
        submit: false,
        capture_text_content: false
      },
      opt_out_tracking_by_default: true,
      disable_persistence: window.localStorage === undefined,
      debug,
      ignore_dnt: true
    });

    mixpanel.reset();
  } catch (e) {
    mixpanel = null;
    console.log('Mixpanel initialization failed', e);
  }
};

const updateTracking = (track: boolean) => {
  if (debug) {
    console.log('mixpanelApi.updateTracking()', track);
  }

  if (!hasDOM || !mixpanel) {
    return;
  }

  try {
    if (track) {
      mixpanel.opt_in_tracking();
    } else {
      mixpanel.opt_out_tracking();
    }
  } catch (e) {
    console.log('Mixpanel updateTracking failed', e);
  }
};

const onLogin = (externalId: string) => {
  if (debug) {
    console.log('mixpanelApi.onLogin()', externalId);
  }

  if (!hasDOM || !mixpanel) {
    return;
  }

  try {
    mixpanel.identify(externalId);
  } catch (e) {
    console.log('Mixpanel identify failed', e);
  }
};

const onLogout = () => {
  if (debug) {
    console.log('mixpanelApi.onLogout()');
  }

  if (!hasDOM || !mixpanel) {
    return;
  }

  try {
    mixpanel.reset();
  } catch (e) {
    console.log('Mixpanel reset failed', e);
  }
};

const event = (name: string, properties: Record<string, unknown> = {}) => {
  if (debug) {
    console.log('mixpanelApi.event()', name, properties);
  }

  if (!hasDOM || !mixpanel) {
    return;
  }

  try {
    mixpanel.track(name, properties);
  } catch (e) {
    console.log('Mixpanel track failed', e);
  }
};

const register = (properties: { [index: string]: GlobalProperties }) => {
  if (debug) {
    console.log('mixpanelApi.register()', properties);
  }

  if (!hasDOM || !mixpanel) {
    return;
  }

  try {
    mixpanel.register(properties);
  } catch (e) {
    console.log('Mixpanel register failed', e);
  }
};

const setProperties = (properties: { [index: string]: GlobalProperties }) => {
  if (debug) {
    console.log('mixpanelApi.setProperties()', properties);
  }

  if (!hasDOM || !mixpanel || !mixpanel.people) {
    return;
  }

  try {
    mixpanel.people.set(properties);
  } catch (e) {
    console.log('Mixpanel people.set failed', e);
  }
};

export const mixpanelApi = {
  init,
  updateTracking,
  onLogin,
  onLogout,
  register,
  event,
  setProperties
};
